import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PageMeta from '../components/PageMeta';
import authService from '../services/auth';
import './Auth.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Input Email/Phone, 2: Verify Code, 3: Reset Password
  
  // Step 1 State
  const [emailOrPhone, setEmailOrPhone] = useState('');
  
  // Step 2 State
  const [userId, setUserId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifiedToken, setVerifiedToken] = useState('');
  
  // Step 3 State
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Common State
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // --- Step 1: Request Reset ---
  const handleEmailPhoneChange = (e) => {
    setEmailOrPhone(e.target.value);
    if (errors.email_or_phone) {
      setErrors({ ...errors, email_or_phone: '' });
    }
  };

  const validateStep1 = () => {
    if (!emailOrPhone) {
      setErrors({ email_or_phone: 'Email or Phone Number is required' });
      return false;
    }
    return true;
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;

    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      const response = await authService.requestPasswordReset({ email_or_phone: emailOrPhone });
      const data = response.data;
      
      setUserId(data.user_id);
      setMaskedEmail(data.masked_email);
      setMaskedPhone(data.masked_phone);
      setMessage(data.message);
      setIsSuccess(true);
      setStep(2); // Move to verification step
    } catch (error) {
      setIsSuccess(false);
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.errors && errorData.errors.email_or_phone) {
          setErrors({ email_or_phone: errorData.errors.email_or_phone[0] });
        } else if (errorData.message) {
          setMessage(errorData.message);
        }
      } else {
        setMessage(error.message || 'Failed to request password reset. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: Verify Code ---
  const handleCodeChange = (index, value) => {
    if (value.length > 1) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`code-${index + 1}`)?.focus();
    }
    setErrors({ ...errors, code: '' });
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`code-${index - 1}`)?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (pastedData.length === 6 && /^\d+$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      document.getElementById('code-5')?.focus();
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setErrors({ code: 'Please enter the complete 6-digit code' });
      return;
    }

    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      const response = await authService.verifyResetCode({
        user_id: userId,
        code: verificationCode
      });
      
      setVerifiedToken(response.data.verified_token);
      setMessage('');
      setIsSuccess(false);
      setStep(3); // Move to password reset step
    } catch (error) {
      setIsSuccess(false);
      if (error.response && error.response.data) {
        if (error.response.data.errors && error.response.data.errors.code) {
           setErrors({ code: error.response.data.errors.code[0] });
        } else {
           setMessage(error.response.data.message || 'Verification failed.');
        }
      } else {
        setMessage('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Step 3: Reset Password ---
  const validateStep3 = () => {
    const newErrors = {};
    if (!password || password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validateStep3()) return;

    setLoading(true);
    setMessage('');
    
    try {
      const response = await authService.resetPassword({
        user_id: userId,
        password: password,
        password_confirmation: confirmPassword,
        verified_token: verifiedToken
      });

      setMessage(response.data.message);
      setIsSuccess(true);
      
      // Redirect to login after short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      setIsSuccess(false);
       if (error.response && error.response.data) {
         if (error.response.data.errors) {
            setErrors(error.response.data.errors); // Might contain password errors
         } else {
            setMessage(error.response.data.message || 'Failed to reset password.');
         }
      } else {
        setMessage('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // --- Render Helpers ---
  const renderStep1 = () => (
    <div className="auth-card">
      <h1>Reset Password</h1>
      <p className="auth-subtitle">
        Enter your registered email or phone number to receive a reset code.
      </p>

      {message && !isSuccess && (
        <div className="error-message">{message}</div>
      )}

      <form onSubmit={handleRequestReset} className="auth-form">
        <div className="form-group">
          <label htmlFor="emailOrPhone">Email or Phone Number *</label>
          <input
            type="text"
            id="emailOrPhone"
            value={emailOrPhone}
            onChange={handleEmailPhoneChange}
            placeholder="e.g. name@example.com or 09012345678"
            className={errors.email_or_phone ? 'error' : ''}
            disabled={loading}
          />
          {errors.email_or_phone && <span className="error-text">{errors.email_or_phone}</span>}
        </div>

        <button type="submit" className="auth-btn" disabled={loading}>
          {loading ? 'Sending Code...' : 'Send Reset Code'}
        </button>

        <div className="auth-links">
          <p>
            Remember your password? <a href="/login">Login here</a>
          </p>
        </div>
      </form>
    </div>
  );

  const renderStep2 = () => (
    <div className="auth-card">
      <h1>Verify Code</h1>
      <p className="auth-subtitle">
        We sent a code to your {maskedEmail && maskedPhone ? 'email and phone' : maskedEmail ? 'email' : 'phone'}.
        <br />
        {maskedEmail && <small>Email: {maskedEmail}<br/></small>}
        {maskedPhone && <small>Phone: {maskedPhone}</small>}
      </p>

      {message && (
        <div className={isSuccess ? 'success-message' : 'error-message'}>
          {message}
        </div>
      )}

      <form onSubmit={handleVerifyCode} className="auth-form">
        <div className="form-group">
          <label>Verification Code *</label>
          <div className="code-inputs">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`code-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={errors.code ? 'error' : ''}
                disabled={loading}
              />
            ))}
          </div>
          {errors.code && <span className="error-text">{errors.code}</span>}
        </div>

        <button type="submit" className="auth-btn" disabled={loading}>
          {loading ? 'Verifying...' : 'Verify Code'}
        </button>
        
        <div className="auth-links">
             <button type="button" className="text-btn" onClick={() => setStep(1)} disabled={loading}>
                Wrong contact info? Try again
             </button>
        </div>
      </form>
    </div>
  );

  const renderStep3 = () => (
    <div className="auth-card">
      <h1>Create New Password</h1>
      <p className="auth-subtitle">
        Please enter a new password for your account.
      </p>

      {message && (
        <div className={isSuccess ? 'success-message' : 'error-message'}>
          {message}
        </div>
      )}

      <form onSubmit={handleResetPassword} className="auth-form">
        <div className="form-group">
          <label htmlFor="password">New Password *</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => {
                setPassword(e.target.value);
                if(errors.password) setErrors({...errors, password: ''});
            }}
            placeholder="Enter new password"
            className={errors.password ? 'error' : ''}
            disabled={loading || isSuccess}
          />
          {errors.password && <span className="error-text">{errors.password}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password *</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => {
                setConfirmPassword(e.target.value);
                if(errors.confirmPassword) setErrors({...errors, confirmPassword: ''});
            }}
            placeholder="Confirm new password"
            className={errors.confirmPassword ? 'error' : ''}
            disabled={loading || isSuccess}
          />
          {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
        </div>

        <button type="submit" className="auth-btn" disabled={loading || isSuccess}>
          {loading ? 'Resetting...' : 'Reset Password'}
        </button>
      </form>
    </div>
  );

  return (
    <Layout>
      <PageMeta
        title="Reset Password"
        description="Reset the password for your FM Ultratech Farms account."
      />
      <div className="auth-page">
        <div className="auth-container">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </Layout>
  );
};

export default ForgotPassword;
